import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { type z, type ZodSchema } from "zod";
import { isNextRedirect } from "@/lib/utils";

type DataService<Values> = {
  submit: (data: Values) => Promise<{ errors?: string[] }>;
};
type NotificationService = {
  success: (message: string) => void;
  error: (message: string) => void;
};
type I18nService = {
  getSuccessMessage: () => string;
  getErrorMessage: () => string;
};

export function submitFormUseCase<Data, Values>(
  dataService: DataService<Values>,
  notificationService: NotificationService,
  i18nService: I18nService,
  transformer: (data: Data) => Values,
) {
  const onSuccess = () => {
    notificationService.success(i18nService.getSuccessMessage());
  };
  const onError = (errors: string[]) => {
    notificationService.error(i18nService.getErrorMessage());
    errors.forEach((error) => {
      notificationService.error(error);
    });
  };
  return {
    execute: async (values: Data) => {
      try {
        const formData = transformer(values);
        const result = await dataService.submit(formData);
        if (!result.errors || result.errors.length === 0) {
          onSuccess();
        } else {
          onError(result.errors);
        }
      } catch (error) {
        // Check if this is a Next.js redirect - if so, don't treat it as an error
        if (isNextRedirect(error)) {
          console.log(
            "🔄 [FORM] Next.js redirect detected, not treating as error",
          );
          return; // Don't show error toast for redirects
        }

        // For all other errors, show the error toast
        onError(error instanceof Error ? [error.message] : ["Unknown error"]);
      }
    },
  };
}

interface UseFormSubmitProps<Values> {
  formService: {
    onSubmit: (data: Values) => Promise<{ errors?: string[] }>;
    schema: ZodSchema;
    defaultValues: z.infer<ZodSchema>;
    transformer: (data: z.infer<ZodSchema>) => Values;
  };
  notificationService: {
    success: (message: string) => void;
    error: (message: string) => void;
  };
  i18nService: {
    getSuccessMessage: () => string;
    getErrorMessage: () => string;
  };
}
export function useFormSubmit<Values>({
  formService,
  notificationService,
  i18nService,
}: UseFormSubmitProps<Values>) {
  const form = useForm({
    // @ts-expect-error - zodResolver is not typed correctly
    resolver: zodResolver(formService.schema),
    defaultValues: formService.defaultValues,
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldUnregister: false,
    criteriaMode: "firstError",
  });
  const {
    handleSubmit,
    formState: { isSubmitting, errors },
  } = form;

  const handleFormSubmit = async (values: z.infer<ZodSchema>) => {
    const useCase = submitFormUseCase(
      {
        submit: formService.onSubmit,
      },
      notificationService,
      i18nService,
      formService.transformer,
    );
    await useCase.execute(values);
  };
  return {
    form,
    handleSubmit: handleSubmit(handleFormSubmit),
    isSubmitting,
    errors,
  };
}
